import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import {
  provideRouter,
  Router,
  TitleStrategy,
  withInMemoryScrolling,
} from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { appRoutes } from './app.routes';
import {
  provideHttpClient,
  withFetch,
  withInterceptors,
} from '@angular/common/http';
import { TranslocoHttpLoader } from './transloco-loader';
import {
  provideTransloco,
  provideTranslocoTranspiler,
  TranslocoService,
} from '@jsverse/transloco';
import { environment } from '.././environments/environment';
import * as Sentry from '@sentry/angular';
import ar from '@angular/common/locales/ar';
import { registerLocaleData } from '@angular/common';
import { authInterceptor } from './common/interceptors/auth.interceptor';
import { errorInterceptor } from './common/interceptors/error.interceptor';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeuix/themes/aura';
import { definePreset } from '@primeuix/themes';
import { BrandingService } from './common/branding/branding.service';
import { BrandingTitleStrategy } from './common/branding/branding-title.strategy';
import { BrandTranspiler } from './common/branding/brand.transpiler';

registerLocaleData(ar);

const pathNameArray: string[] = location.pathname.split('/');
const savedLang: string = localStorage.getItem('lang') ?? 'ar';

if (pathNameArray[1].includes('en') || pathNameArray[1].includes('ar')) {
  localStorage.setItem('lang', pathNameArray[1]);
} else if (!localStorage.getItem('lang')) {
  localStorage.setItem('lang', 'ar');
}

// PrimeNG's primary follows the app's brand scale, which `BrandingService`
// re-points at runtime — so PrimeNG widgets pick up the replica's colour too.
const BrandedAura = definePreset(Aura, {
  semantic: {
    primary: {
      50: 'var(--color-supplier-50)',
      100: 'var(--color-supplier-100)',
      200: 'var(--color-supplier-200)',
      300: 'var(--color-supplier-300)',
      400: 'var(--color-supplier-400)',
      500: 'var(--color-supplier-500)',
      600: 'var(--color-supplier-600)',
      700: 'var(--color-supplier-700)',
      800: 'var(--color-supplier-800)',
      900: 'var(--color-supplier-900)',
      950: 'var(--color-supplier-900)',
    },
  },
});

export const INITIAL_LANGUAGE: string =
  pathNameArray[1].includes('en') || pathNameArray[1].includes('ar')
    ? pathNameArray[1]
    : savedLang;

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideAnimations(),
    provideRouter(
      appRoutes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled',
      })
    ),
    { provide: TitleStrategy, useExisting: BrandingTitleStrategy },
    provideAppInitializer(() => inject(BrandingService).init()),
    provideHttpClient(
      withFetch(),
      withInterceptors([authInterceptor, errorInterceptor])
    ),
    provideTransloco({
      config: {
        availableLangs: ['en', 'ar'],
        defaultLang: INITIAL_LANGUAGE,
        reRenderOnLangChange: true,
        prodMode: environment.production,
      },
      loader: TranslocoHttpLoader,
    }),
    provideTranslocoTranspiler(BrandTranspiler),
    provideAppInitializer(() => {
      const transloco = inject<TranslocoService>(TranslocoService);
      transloco.setActiveLang(INITIAL_LANGUAGE);
      return transloco.load(INITIAL_LANGUAGE);
    }),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
    providePrimeNG({
      theme: {
        preset: BrandedAura,
        options: {
          darkModeSelector: false,
        },
      },
    }),
  ],
};
